import { useCallback, useMemo, useState } from 'react'
import {
    Combobox,
    ComboboxItem,
    ComboboxPopover,
    ComboboxProvider,
    Portal,
    useComboboxStore,
} from '@ariakit/react'
import { Avatar } from '@components/Avatar/Avatar'
import { CSVUploader } from '@components/CSVUploader/CSVUploader'
import { Box, BoxProps, Stack, Text } from '@ui'
import fuzzysort from 'fuzzysort'
import { useDevice } from 'hooks/useDevice'
import { shortAddress } from 'ui/utils/utils'
import {
    Address,
    useGetRootKeyFromLinkedWallet,
    useSpaceMembers,
    useUserLookupArray,
} from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { isAddress } from 'viem'

import { WalletMemberList } from './WalletMemberList'
import * as styles from './WalletMemberSelector.css'

type Props = {
    isRole: boolean | undefined
    walletMembers: Address[]
    onChange: (addresses: Address[]) => void
    isValidationError: boolean
} & Pick<BoxProps, 'background'>

export const WalletMemberSelector = (props: Props) => {
    const { walletMembers, onChange, isValidationError, isRole } = props
    const { memberIds } = useSpaceMembers()
    const users = useUserLookupArray(memberIds)
    const [searchTerm, setSearchTerm] = useState('')
    const { isTouch } = useDevice()

    const combobox = useComboboxStore({
        value: searchTerm,
        setValue: setSearchTerm,
        resetValueOnHide: true,
    })

    const handleCSVAddresses = useCallback(
        (addresses: Address[]) => {
            const newAddresses = [...new Set([...walletMembers, ...addresses])]
            onChange(newAddresses)
        },
        [walletMembers, onChange],
    )

    const handleRemoveAddress = useCallback(
        (addressToRemove: Address) => {
            onChange(walletMembers.filter((address) => address !== addressToRemove))
        },
        [walletMembers, onChange],
    )

    const handleRemoveAllAddresses = useCallback(() => {
        onChange([])
    }, [onChange])

    const handleAddAddress = useCallback(
        (newAddress: Address) => {
            if (!walletMembers.includes(newAddress)) {
                onChange([...walletMembers, newAddress])
            }
            combobox.setValue('')
            combobox.hide()
        },
        [walletMembers, onChange, combobox],
    )

    const { data: rootKeyFromSearchTerm } = useGetRootKeyFromLinkedWallet({
        walletAddress: searchTerm as Address | undefined,
    })

    const filteredUsers = useMemo(() => {
        if (!users) {
            return []
        }
        const candidates = users.map((user) => ({
            user,
            prettyName: getPrettyDisplayName(user),
            username: user.username ?? '',
        }))
        const fuzzyResults = fuzzysort
            .go(searchTerm, candidates, {
                keys: ['prettyName', 'username'],
                all: true,
            })
            .map((result) => result.obj.user)
        const rootKeyMatches =
            rootKeyFromSearchTerm && searchTerm
                ? users.filter((user) =>
                      user.userId.toLowerCase().includes(rootKeyFromSearchTerm.toLowerCase()),
                  )
                : []
        const combined = [...fuzzyResults, ...rootKeyMatches]
        const uniqueUsers = Array.from(
            new Map(combined.map((user) => [user.userId, user])).values(),
        )
        return uniqueUsers.filter((user) => !walletMembers.includes(user.userId as Address))
    }, [users, searchTerm, rootKeyFromSearchTerm, walletMembers])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                if (isAddress(searchTerm)) {
                    handleAddAddress((rootKeyFromSearchTerm || searchTerm) as Address)
                    return
                }
            }
        },
        [handleAddAddress, searchTerm, rootKeyFromSearchTerm],
    )

    return (
        <ComboboxProvider store={combobox}>
            <Stack gap data-testid="user-search">
                <Stack gap="sm">
                    <Box position="relative">
                        <Box horizontal gap="sm" alignItems="center">
                            <Box
                                background={props.background ?? 'level3'}
                                rounded="sm"
                                style={{ flex: 1 }}
                            >
                                <Combobox
                                    autoSelect="always"
                                    autoComplete="none"
                                    placeholder={
                                        isRole
                                            ? 'Search members or enter address'
                                            : 'Enter wallet address'
                                    }
                                    data-testid="address-selection-input"
                                    className={styles.comboboxInput}
                                    onKeyDown={handleKeyDown}
                                />
                            </Box>
                            {!isTouch && (
                                <CSVUploader
                                    handleCSVAddresses={handleCSVAddresses}
                                    background={props.background ?? 'level3'}
                                />
                            )}
                        </Box>
                        {searchTerm && (
                            <Portal>
                                <ComboboxPopover
                                    sameWidth
                                    gutter={8}
                                    className={styles.comboboxPopover}
                                >
                                    <Stack gap="xs">
                                        {filteredUsers.length > 0 ? (
                                            filteredUsers.map((user) => (
                                                <ComboboxItem
                                                    resetValueOnSelect
                                                    key={user.userId}
                                                    value={user.userId}
                                                    data-testid={`address-selection-option-${user.userId}`}
                                                    className={styles.comboboxItem}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        handleAddAddress(user.userId as Address)
                                                    }}
                                                >
                                                    <Avatar size="avatar_sm" userId={user.userId} />
                                                    <Text>{getPrettyDisplayName(user)}</Text>
                                                </ComboboxItem>
                                            ))
                                        ) : isAddress(searchTerm) ? (
                                            <ComboboxItem
                                                resetValueOnSelect
                                                value={searchTerm}
                                                data-testid={`address-selection-option-${searchTerm}`}
                                                className={styles.comboboxItem}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    handleAddAddress(searchTerm as Address)
                                                }}
                                            >
                                                <Text>
                                                    Add address:{' '}
                                                    {shortAddress(searchTerm as Address)}
                                                </Text>
                                            </ComboboxItem>
                                        ) : (
                                            <Box padding>
                                                <Text color="gray2">
                                                    {isRole
                                                        ? 'Not a member or valid address'
                                                        : 'Not a valid address'}
                                                </Text>
                                            </Box>
                                        )}
                                    </Stack>
                                </ComboboxPopover>
                            </Portal>
                        )}
                    </Box>
                    <WalletMemberList
                        walletMembers={walletMembers}
                        isRole={isRole}
                        onRemove={handleRemoveAddress}
                        onRemoveAll={handleRemoveAllAddresses}
                    />
                </Stack>
                {isValidationError && (
                    <Text color="error">Please add at least one valid address.</Text>
                )}
            </Stack>
        </ComboboxProvider>
    )
}
